// ProtectedRoute.js
import React, { useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "./store/authStore";

// True after the zustand `persist` middleware has finished merging
// localStorage into the store. Subscribes via the official persist API so
// the component re-renders the instant rehydration finishes — no manual
// flag-on-state that can desync after login. Initialised greedily so that
// when the store was already hydrated before this component mounted
// (typical for in-app navigations) we don't flash the spinner at all.
function useAuthHydrated() {
  const [hydrated, setHydrated] = useState(
    () => useAuthStore.persist?.hasHydrated?.() ?? true,
  );
  useEffect(() => {
    if (!useAuthStore.persist) return undefined;
    // If hydration already finished between render and effect, sync up.
    if (useAuthStore.persist.hasHydrated()) {
      setHydrated(true);
      return undefined;
    }
    const unsub = useAuthStore.persist.onFinishHydration(() => setHydrated(true));
    return () => unsub?.();
  }, []);
  return hydrated;
}

const ProtectedRoute = ({ children }) => {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const user = useAuthStore(state => state.user);
  const hydrated = useAuthHydrated();
  const location = useLocation();

  // Hold off on the redirect decision until the persisted auth store has
  // hydrated. Without this, a deep link (e.g. /report-list/<user>/<id>
  // opened from chat or email) flashes to /login on the very first render
  // — zustand `persist` populates state on the next tick, so the initial
  // `isAuthenticated` is `false` even when localStorage has a valid
  // session.
  if (!hydrated) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  // If user is NOT logged in, redirect to "/"
  if (!isAuthenticated || !user) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  // If user IS logged in, render the protected page
  return children;
};

export default ProtectedRoute;
